import { useMemo, useEffect } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { EnabledDisabled } from 'modules/Settings/constant';
import {
  GroupType,
  CollectorGroupSettings,
  Group,
  CollectorSettingQuery
} from 'graphql/types';

export type GroupsResult = Record<
  GroupType,
  { id: string; displayText: string }[]
>;
export type CollectorRowSettings = GroupsResult & {
  enableDisabled: EnabledDisabled;
  optedIn: EnabledDisabled;
};

export type DefaultRow = {
  enableDisabled: EnabledDisabled;
  optedIn: EnabledDisabled;
};

export type FormData = {
  defaultRow: DefaultRow;
  rows: CollectorRowSettings[];
};

export interface ResultProps {
  formMethods: UseFormReturn<FormData>;
  defaultEnabled: EnabledDisabled;
  defaultOptedIn: EnabledDisabled;
}

export const mapToUiAccessSope = (
  groups: Group[]
): { id: string; displayText: string }[] => {
  return groups
    ? groups.map(({ id, name }) => ({
        id: id!,
        displayText: name!
      }))
    : [];
};

export const mapToAccessSope = (
  key: string,
  groups: any[]
):
  | {
      [x: string]: Group[];
    }
  | undefined => {
  return groups
    ? {
        [key]: groups.map(({ id, displayText }) => ({
          id: id!,
          name: displayText!
        }))
      }
    : undefined;
};

const mapToDefaultValues = (
  rows: CollectorGroupSettings[]
): CollectorRowSettings[] =>
  rows.map(({ accessScopeIn, enabled, canOptOut }: CollectorGroupSettings) => {
    const {
      departments,
      employeeTypes,
      locations,
      divisions,
      costCenters,
      serviceCenters
    } = accessScopeIn || {};
    return {
      enableDisabled: enabled
        ? EnabledDisabled.enabled
        : EnabledDisabled.disabled,
      optedIn: canOptOut ? EnabledDisabled.enabled : EnabledDisabled.disabled,
      [GroupType.Department]: mapToUiAccessSope(departments ?? []),
      [GroupType.EmployeeType]: mapToUiAccessSope(employeeTypes ?? []),
      [GroupType.Location]: mapToUiAccessSope(locations ?? []),
      [GroupType.Division]: mapToUiAccessSope(divisions ?? []),
      [GroupType.CostCenter]: mapToUiAccessSope(costCenters ?? []),
      [GroupType.ServiceCenter]: mapToUiAccessSope(serviceCenters ?? [])
    };
  });

export const useCompanyCollectorSettingsForm = (
  data?: CollectorSettingQuery
): ResultProps => {
  const defaultValues = useMemo(() => {
    const groupSettings = data?.collectorSetting?.item?.groupSettings ?? [];
    const settings = data?.collectorSetting?.item?.settings ?? [];
    const rows = mapToDefaultValues(groupSettings);

    const collectorEnabled = settings.find(
      (x) => x?.key === 'collectorEnabled'
    );
    const allowUsersToOptOutEnabled = settings.find(
      (x) => x?.key === 'allowUsersToOptOutEnabled'
    );
    return {
      rows,
      defaultRow: {
        enableDisabled: collectorEnabled
          ? collectorEnabled.value === 'true'
            ? EnabledDisabled.enabled
            : EnabledDisabled.disabled
          : EnabledDisabled.enabled,
        optedIn: allowUsersToOptOutEnabled
          ? allowUsersToOptOutEnabled.value === 'true'
            ? EnabledDisabled.enabled
            : EnabledDisabled.disabled
          : EnabledDisabled.enabled
      }
    };
  }, [data]);

  const formMethods = useForm({
    defaultValues
  });

  const { getValues, reset } = formMethods;
  useEffect(() => {
    const values = getValues();
    if (defaultValues.rows) {
      reset(
        {
          ...values,
          rows: defaultValues.rows,
          defaultRow: defaultValues.defaultRow
        },
        {
          keepErrors: true,
          keepDirty: true
        }
      );
    }
  }, [defaultValues.defaultRow, defaultValues.rows, getValues, reset]);

  return {
    formMethods,
    defaultEnabled: defaultValues.defaultRow.enableDisabled,
    defaultOptedIn: defaultValues.defaultRow.optedIn
  };
};
